import React, { useEffect, useState } from 'react';
import { WifiOff, AlertTriangle, X, RefreshCw } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { mapServiceError } from '../utils/serviceErrors';

/**
 * Global banner for offline mode and data-fetch failures.
 * Shown above page content; complements page-level DataFetchState.
 */
const ServiceStatusBanner = () => {
  const {
    caregiversError,
    userDataError,
    fetchCaregivers,
    refreshUserData,
  } = useData();

  const [offline, setOffline] = useState(!navigator.onLine);
  const [dismissed, setDismissed] = useState(null);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => {
      setOffline(false);
      setDismissed(null);
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const serviceError = caregiversError || userDataError;
  const errorKey = serviceError || (offline ? 'offline' : null);

  useEffect(() => {
    setDismissed(null);
  }, [errorKey]);

  if (!offline && !serviceError) return null;
  if (dismissed === errorKey && !offline) return null;

  const handleRetry = () => {
    if (offline) {
      window.location.reload();
      return;
    }
    if (caregiversError) fetchCaregivers();
    if (userDataError) refreshUserData();
    setDismissed(null);
  };

  const mapped = serviceError ? mapServiceError(serviceError) : null;
  const showRetry = offline || serviceError;

  return (
    <div
      className={`pf-status-banner ${offline ? 'pf-status-banner--offline' : 'pf-status-banner--error'}`}
      role="status"
      aria-live="polite"
    >
      {offline ? (
        <WifiOff className="w-4 h-4 shrink-0" aria-hidden="true" />
      ) : (
        <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
      )}
      <span className="pf-status-banner__text">
        {offline
          ? "You're offline — some features may be unavailable"
          : mapped?.message || "We couldn't load your data"}
      </span>
      {showRetry ? (
        <button
          type="button"
          onClick={handleRetry}
          className="pf-status-banner__action"
          aria-label="Retry loading data"
        >
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
          Retry
        </button>
      ) : null}
      {!offline && serviceError ? (
        <button
          type="button"
          onClick={() => setDismissed(errorKey)}
          className="pf-status-banner__dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );
};

export default ServiceStatusBanner;
